import React, { useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import VideoCallOutlinedIcon from "@mui/icons-material/VideoCallOutlined";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutlineOutlined";
import {
  createAboutUsTutorial,
  deleteAboutUsTutorial,
  getAboutUsTutorials,
  reorderAboutUsTutorials,
  updateAboutUsTutorial,
} from "../../api/aboutUsService";

// Must match AboutUsController on the API.
const VIDEO_TYPES = ["video/mp4", "video/quicktime", "video/webm"];
const MAX_VIDEO_BYTES = 300 * 1024 * 1024;
const MAX_TITLE = 150;
const MAX_DESCRIPTION = 2000;

const formatMb = (bytes) => `${(bytes / 1024 / 1024).toFixed(1)} MB`;

function errorText(err, fallback) {
  const data = err?.response?.data;
  if (typeof data === "string" && data) return data;
  if (data?.title) return data.title;
  return fallback;
}

function TextFields({ title, description, onTitle, onDescription, disabled, autoFocus }) {
  return (
    <Stack spacing={2}>
      <TextField
        label="Title"
        value={title}
        onChange={(e) => onTitle(e.target.value.slice(0, MAX_TITLE))}
        fullWidth
        required
        autoFocus={autoFocus}
        disabled={disabled}
        placeholder="e.g. How to add a product"
        helperText={`${title.length}/${MAX_TITLE}`}
      />
      <TextField
        label="Description (optional)"
        value={description}
        onChange={(e) => onDescription(e.target.value.slice(0, MAX_DESCRIPTION))}
        multiline
        minRows={3}
        fullWidth
        disabled={disabled}
        placeholder="What does this video explain?"
        helperText={`${description.length}/${MAX_DESCRIPTION}`}
      />
    </Stack>
  );
}

function Composer({ onPublished }) {
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [progress, setProgress] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const inputRef = useRef(null);

  // Release the object URL when the file changes or the page unmounts.
  useEffect(() => {
    if (!file) {
      setPreviewUrl("");
      return undefined;
    }
    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  function pickFile(fileList) {
    const picked = fileList?.[0];
    if (!picked) return;
    if (!VIDEO_TYPES.includes(picked.type)) {
      setError(`${picked.name}: not a supported video (MP4, MOV, WEBM).`);
      return;
    }
    if (picked.size > MAX_VIDEO_BYTES) {
      setError(`${picked.name}: ${formatMb(picked.size)} is over the ${formatMb(MAX_VIDEO_BYTES)} limit.`);
      return;
    }
    setError("");
    setFile(picked);
  }

  async function publish() {
    if (!file || !title.trim()) return;
    setError("");
    setProgress(0);
    try {
      await createAboutUsTutorial(file, title.trim(), description.trim(), (p) => setProgress(p));
      setFile(null);
      setTitle("");
      setDescription("");
      onPublished();
    } catch (err) {
      setError(errorText(err, "Upload failed. Please try again."));
    } finally {
      setProgress(null);
    }
  }

  const uploading = progress !== null;

  return (
    <Paper variant="outlined" sx={{ p: { xs: 2, md: 3 }, mb: 4, borderRadius: 3 }}>
      <Typography variant="h6">New how-to video</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        One video with a title and a short description, e.g. how to add a product. Any shape works - phone recordings
        (9:16) and screen recordings (16:9) are both shown in full.
      </Typography>

      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "minmax(0, 320px) 1fr" }, gap: 3, alignItems: "start" }}>
        <Box
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (!uploading) pickFile(e.dataTransfer.files);
          }}
          sx={{
            p: 2,
            borderRadius: 2,
            border: "2px dashed",
            borderColor: dragOver ? "primary.main" : "divider",
            bgcolor: dragOver ? "action.hover" : "transparent",
            transition: "all .2s",
          }}
        >
          {file ? (
            <Stack spacing={1}>
              <Box
                component="video"
                src={previewUrl}
                controls
                playsInline
                preload="metadata"
                sx={{ width: "100%", maxHeight: 320, borderRadius: 1.5, bgcolor: "#0b1f19", display: "block" }}
              />
              <Typography variant="caption" color="text.secondary" noWrap title={file.name}>
                {file.name} · {formatMb(file.size)}
              </Typography>
              <Button size="small" onClick={() => inputRef.current?.click()} disabled={uploading}>
                Choose another video
              </Button>
            </Stack>
          ) : (
            <Stack alignItems="center" spacing={1.5} sx={{ py: 4, textAlign: "center" }}>
              <VideoCallOutlinedIcon color="action" sx={{ fontSize: 44 }} />
              <Typography variant="body1">Drag & drop a video here</Typography>
              <Typography variant="caption" color="text.secondary">
                MP4, MOV or WEBM · max {formatMb(MAX_VIDEO_BYTES)}
              </Typography>
              <Button variant="contained" onClick={() => inputRef.current?.click()}>
                Choose video
              </Button>
            </Stack>
          )}
          <input
            ref={inputRef}
            type="file"
            hidden
            accept={VIDEO_TYPES.join(",")}
            onChange={(e) => {
              pickFile(e.target.files);
              e.target.value = "";
            }}
          />
        </Box>

        <TextFields title={title} description={description} onTitle={setTitle} onDescription={setDescription} disabled={uploading} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      {uploading && (
        <Box sx={{ mt: 2 }}>
          <LinearProgress variant={progress < 1 ? "determinate" : "indeterminate"} value={progress * 100} />
          <Typography variant="caption" color="text.secondary">
            {progress < 1 ? `Uploading… ${Math.round(progress * 100)}%` : "Saving to Azure…"}
          </Typography>
        </Box>
      )}

      <Stack direction="row" justifyContent="flex-end" sx={{ mt: 2 }}>
        <Button variant="contained" onClick={publish} disabled={!file || !title.trim() || uploading}>
          {uploading ? "Publishing…" : "Publish to About Us"}
        </Button>
      </Stack>
    </Paper>
  );
}

function EditDialog({ tutorial, onClose, onSaved }) {
  const [title, setTitle] = useState(tutorial.title || "");
  const [description, setDescription] = useState(tutorial.description || "");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  async function save() {
    setSaving(true);
    setError("");
    try {
      await updateAboutUsTutorial(tutorial.aboutUsTutorialId, title.trim(), description.trim());
      onSaved();
    } catch (err) {
      setError(errorText(err, "Could not save the changes."));
      setSaving(false);
    }
  }

  return (
    <Dialog open onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Edit how-to video</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box sx={{ mt: 1 }}>
          <TextFields title={title} description={description} onTitle={setTitle} onDescription={setDescription} disabled={saving} autoFocus />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button variant="contained" onClick={save} disabled={saving || !title.trim()}>
          Save
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function AboutUsTutorials() {
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  async function reload() {
    const data = await getAboutUsTutorials();
    setTutorials(Array.isArray(data) ? data : []);
  }

  useEffect(() => {
    reload()
      .catch(() => setError("Could not load the how-to videos. Is the API running?"))
      .finally(() => setLoading(false));
  }, []);

  async function move(index, delta) {
    const target = index + delta;
    if (target < 0 || target >= tutorials.length) return;
    const next = [...tutorials];
    [next[index], next[target]] = [next[target], next[index]];
    setTutorials(next);
    setBusy(true);
    try {
      await reorderAboutUsTutorials(next.map((t) => t.aboutUsTutorialId));
    } catch (err) {
      setError(errorText(err, "Could not save the new order."));
      await reload().catch(() => {});
    } finally {
      setBusy(false);
    }
  }

  async function remove(tutorial) {
    if (!window.confirm(`Delete "${tutorial.title}"? It is removed from the About Us page and Azure storage. This cannot be undone.`)) return;
    try {
      await deleteAboutUsTutorial(tutorial.aboutUsTutorialId);
      await reload();
    } catch (err) {
      setError(errorText(err, "This video could not be deleted."));
    }
  }

  return (
    <Box>
      <Composer onPublished={() => reload().catch(() => {})} />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
          {error}
        </Alert>
      )}

      <Typography variant="h6" sx={{ mb: 2 }}>
        Published ({tutorials.length})
      </Typography>

      {loading ? (
        <LinearProgress />
      ) : tutorials.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ p: 2 }}>
          Nothing published yet. The how-to section stays hidden on the About Us page until you add a video.
        </Typography>
      ) : (
        <Stack spacing={1.5}>
          {tutorials.map((tutorial, index) => (
            <Paper
              key={tutorial.aboutUsTutorialId}
              variant="outlined"
              sx={{ p: 1.5, borderRadius: 2, display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 2 }}
            >
              <Box
                component="video"
                src={`${tutorial.videoUrl}#t=0.1`}
                controls
                playsInline
                preload="metadata"
                sx={{
                  width: { xs: "100%", sm: 220 },
                  height: { xs: "auto", sm: 140 },
                  maxHeight: 260,
                  flexShrink: 0,
                  objectFit: "contain",
                  borderRadius: 1.5,
                  bgcolor: "#0b1f19",
                }}
              />
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 0.5 }}>
                  <Chip size="small" label={index + 1} color="primary" />
                  <Typography variant="subtitle1" dir="auto" noWrap sx={{ fontWeight: 600 }}>
                    {tutorial.title}
                  </Typography>
                </Stack>
                <Typography
                  variant="body2"
                  color={tutorial.description ? "text.primary" : "text.secondary"}
                  dir="auto"
                  sx={{ display: "-webkit-box", WebkitLineClamp: 3, WebkitBoxOrient: "vertical", overflow: "hidden", whiteSpace: "pre-line" }}
                >
                  {tutorial.description || "No description"}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {new Date(tutorial.createdAt).toLocaleDateString()}
                </Typography>
              </Box>
              <Stack direction={{ xs: "row", sm: "column" }} justifyContent="space-between" alignItems="flex-end">
                <Box>
                  <Tooltip title="Move earlier">
                    <span>
                      <IconButton size="small" onClick={() => move(index, -1)} disabled={busy || index === 0}>
                        <ArrowUpwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Move later">
                    <span>
                      <IconButton size="small" onClick={() => move(index, 1)} disabled={busy || index === tutorials.length - 1}>
                        <ArrowDownwardIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
                <Box>
                  <Tooltip title="Edit title & description">
                    <IconButton size="small" onClick={() => setEditing(tutorial)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete video">
                    <IconButton size="small" color="error" onClick={() => remove(tutorial)}>
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Stack>
            </Paper>
          ))}
        </Stack>
      )}

      {editing && (
        <EditDialog
          tutorial={editing}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            reload().catch(() => {});
          }}
        />
      )}
    </Box>
  );
}
